<template>
  <Button
    icon="pi pi-comment"
    class="p-button-sm p-button-rounded"
    :class="pratica.appuntamento ? 'p-button-warning' : 'p-button-secondary'"
    v-tooltip.left="tooltip"
    @click="open"
  />

  <Dialog
    v-model:visible="visible"
    header="Richiesta appuntamento"
    :modal="true"
    :style="{ width: '600px' }"
  >
    <p>
      <strong>Fornitura:</strong>
      {{ supplyCode || '-' }}
    </p>

    <div v-if="pratica.appuntamento_id">
      <p>
        <strong>Richiesta attuale:</strong>
      </p>

      <Textarea v-model="testo" rows="5" class="w-full" />

      <div class="mt-3 text-sm">
        <p>
          <strong>Creata da:</strong>
          {{ pratica.appuntamento_created_by_name || '-' }}
        </p>

        <p>
          <strong>Creata il:</strong>
          {{ formatDataOra(pratica.appuntamento_creation_time) }}
        </p>

        <p v-if="pratica.appuntamento_updated_time">
          <strong>Modificata da:</strong>
          {{ pratica.appuntamento_updated_by_name || '-' }}
        </p>

        <p v-if="pratica.appuntamento_updated_time">
          <strong>Ultima modifica:</strong>
          {{ formatDataOra(pratica.appuntamento_updated_time) }}
        </p>
      </div>
    </div>

    <div v-else>
      <p>Non ci sono richieste di appuntamento attive per questa pratica.</p>

      <Textarea
        v-model="testo"
        rows="5"
        class="w-full"
        placeholder="Inserisci una nuova richiesta di appuntamento"
      />
    </div>

    <template #footer>
      <Button label="Annulla" class="p-button-text" @click="visible = false" />

      <Button
        v-if="pratica.appuntamento_id"
        label="Elimina richiesta"
        icon="pi pi-trash"
        class="p-button-danger"
        :loading="saving"
        @click="elimina"
      />

      <Button
        v-if="pratica.appuntamento_id"
        label="Modifica richiesta"
        icon="pi pi-save"
        :disabled="!testo.trim()"
        :loading="saving"
        @click="aggiorna"
      />

      <Button
        v-else
        label="Aggiungi richiesta"
        icon="pi pi-plus"
        :disabled="!testo.trim()"
        :loading="saving"
        @click="aggiungi"
      />
    </template>
  </Dialog>
</template>

<script>
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";
import Service from "@/services/ask-pratiche/gestione-pratiche";

const CAMPI_APPUNTAMENTO = [
  "appuntamento_id",
  "appuntamento",
  "appuntamento_created_by",
  "appuntamento_created_by_name",
  "appuntamento_creation_time",
  "appuntamento_updated_by",
  "appuntamento_updated_by_name",
  "appuntamento_updated_time",
];

export default {
  name: "AppuntamentoPratica",
  props: {
    // riga della lista: i campi appuntamento_* vengono aggiornati qui, senza ricaricare la tabella
    pratica: {
      type: Object,
      required: true,
    },
    // chiave di PraticheAppuntamentiController::TIPI_PRATICA (ask_pratiche)
    tipoPratica: {
      type: String,
      required: true,
    },
    supplyCode: {
      type: String,
      default: null,
    },
    // id della pratica, se nella riga non è in "id" (es. sezione Pratiche di In Call: "pratica_id")
    praticaId: {
      type: [Number, String],
      default: null,
    },
    // tooltip con il testo della richiesta invece dell'azione (In Call, dove non c'è la colonna col testo)
    tooltipConTesto: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const service = new Service();
    const toast = useToast();
    const visible = ref(false);
    const saving = ref(false);
    const testo = ref("");

    const tooltip = computed(() => {
      if (props.tooltipConTesto) {
        return props.pratica.appuntamento || "Nessuna Richiesta di Appuntamento";
      }
      return props.pratica.appuntamento ? "Richiesta appuntamento" : "Aggiungi richiesta appuntamento";
    });

    const open = () => {
      testo.value = props.pratica.appuntamento || "";
      visible.value = true;
    };

    const aggiornaRiga = (data) => {
      CAMPI_APPUNTAMENTO.forEach((campo) => {
        // eslint-disable-next-line vue/no-mutating-props
        props.pratica[campo] = data ? data[campo] ?? null : null;
      });
    };

    const esegui = async (chiamata, datiRiga) => {
      saving.value = true;
      try {
        const res = await chiamata();
        if (!res?.success) {
          toast.add({ severity: "error", summary: "Errore", detail: res?.message || "Operazione non riuscita", life: 4000 });
          return;
        }
        aggiornaRiga(datiRiga(res));
        toast.add({ severity: "success", summary: "Richiesta appuntamento", detail: res.message, life: 3000 });
        visible.value = false;
      } catch (error) {
        toast.add({ severity: "error", summary: "Errore", detail: error?.message || "Operazione non riuscita", life: 4000 });
      } finally {
        saving.value = false;
      }
    };

    const aggiungi = () =>
      esegui(
        () =>
          service.aggiungiAppuntamentoPratica({
            tipo_pratica: props.tipoPratica,
            pratica_id: props.praticaId ?? props.pratica.id,
            appuntamento: testo.value,
            supply_code: props.supplyCode,
          }),
        (res) => res.data
      );

    const aggiorna = () =>
      esegui(
        () =>
          service.aggiornaAppuntamentoPratica({
            id: props.pratica.appuntamento_id,
            appuntamento: testo.value,
          }),
        (res) => res.data
      );

    const elimina = () =>
      esegui(() => service.eliminaAppuntamentoPratica(props.pratica.appuntamento_id), () => null);

    // "YYYY-MM-DD HH:mm:ss" dal DB -> "DD/MM/YYYY HH:mm", senza passare da Date per non spostare il fuso
    const formatDataOra = (value) => {
      if (!value) return "-";
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
      return match ? `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}` : value;
    };

    return {
      tooltip,
      visible,
      saving,
      testo,
      open,
      aggiungi,
      aggiorna,
      elimina,
      formatDataOra,
    };
  },
};
</script>
